import { Dayjs } from 'dayjs';
import { Shadow, HTMLSpacing } from './Style';

export interface CSSStyleI {
  [key: string]: string;
}

export interface ChartPropsI {
  axis: AxisOpts[] | null;
  data: DataI[] | null;
  bounds: BoundsI | null;
  dataInput?: string;
  style?: CSSStyleI;
}

export interface BoundsI {
  [key: string]: number;
  width: number;
  height: number;
}

export interface TTQuill {
  enabled?: boolean;
  quill: string;
  quillBackground?: any;
  valueStyle?: any;
}

export interface CustomQuill extends TTQuill {
  id?: string;
  touched?: boolean;
  locked?: boolean;
  priority?: boolean;
  line?: boolean;
  lineStyles?: LineStyle;
  tick?: boolean;
  tickStyles?: LineStyle;
  connectingLines?: boolean;
  connectingLinesStyle?: LineStyle;
}

export interface TTBorder {
  size: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  color: string;
  type: string;
}

export interface MarginI {
  top: number;
  topUnit?: string;
  right: number;
  rightUnit?: string;
  bottom: number;
  bottomUnit?: string;
  left: number;
  leftUnit?: string;
}

export interface PaddingI {
  top?: number;
  topUnit?: string;
  right?: number;
  rightUnit?: string;
  bottom?: number;
  bottomUnit?: string;
  left?: number;
  leftUnit?: string;
}

export interface LineOpts {
  id: string;
  label?: string;
  touched?: boolean;
  locked?: boolean;
  styles?: LineStyle;
  animate?: boolean;
}

export type DashType = 'solid' | 'dotted' | 'dashed' | 'double-dashed';

export type DotDisplay = 'all' | 'last' | 'first' | 'first-last';

export type DotShape = 'circle' | 'square' | 'triangle-up' | 'triangle-down' | 'cross' | 'star';

export type EmptyLine = 'default' | 'empty' | 'custom';

export type ShapeRendering = 'auto' | 'optimizeSpeed' | 'crispEdges' | 'geometricPrecision';

export interface LineStyle {
  stroke?: string; // Color
  useDataColor?: boolean;
  strokeWidth?: number; // Thickness
  // strokeDasharray?: [number, number] // [dash, gap]
  strokeDasharray?: DashType;
  curve?: string;
  dots?: {
    enabled?: boolean;
    display: DotDisplay;
    shape: DotShape;
    size: number;
    stroke?: string; // Color
    fill?: string; // Fill Color
    strokeWidth?: number;
    useDataColor?: boolean; // Fill
    useDataColorStroke?: boolean;
  };
  shapeRendering?: ShapeRendering;
  animate?: boolean;
}
export interface SVGPathI {
  marker: string; // M | L | Z | C
  values: number[];
}

export interface SVGPointI {
  x: number;
  y: number;
}

export interface SVGBezier {
  a: SVGPointI; // First Point Coordinates
  b: SVGPointI; // First Helper Point
  c: SVGPointI; // Second Helper Point
  d: SVGPointI; // Second Point Coordinates
}


export interface SliderPropsI {
  enabled: boolean;
  min: number;
  max: number;
}

export interface ControlPropsI {
  enabled: boolean;
}

export interface AreaOpts {
  id: string;
  from?: string; // Label of the Line
  to?: string; // Label of the Line
  touched?: boolean;
  locked?: boolean;
  styles?: AreaStyle;
}

export interface AreaStyle {
  // Exp
  background?: {
    backgroundColor?: string; // Color
    backgroundImage?: string; // Image
  };
  negative?: {
    backgroundColor?: string; // Color
    backgroundImage?: string; // Image
  };

  fill?: string; // Color
  fillOpacity?: number; // Opacity
  negativeEnabled?: boolean; // Boolean
  negativeFill?: string; // Negative Color
  negativeOpacity?: number; // Negative Opacity
  curve?: string;
}

export interface AnimationPropsI {
  enabled: boolean;
  autoplay: boolean;
  duration: number;
  slider: SliderPropsI;
  controls: ControlPropsI;
  axisBehavior: string; // 'race' | 'reveal',
  type: string; // 'discrete' | 'continuos'
  margin: MarginI;
  style: {
    color: string;
    playback: {
      size: number;
    };
    slider: {
      tooltips: {
        backgroundColor: string;
        style: any;
      };
    };
  };
}

export interface AxisOpts {
  // Basic
  id?: string;
  enabled: boolean;
  field: string;
  type: string;
  title: TTQuill;
  // Configs
  interpolate?: boolean; // User Config
  hideDuplicates?: boolean;
  hasInterpolation?: boolean; // Determined on date axis analysis
  // Rendering
  direction: string;
  position: string;
  offset?: number; // Initial Size
  padding?: AxisPaddingI;
  format?: any;
  rotation?: number;
  // Labels Items
  labelsQuill: TTQuill;
  firstPlacement: string;
  lastPlacement: string;

  // Axis Labels display
  labelsShowMode?: string; // 'visibleData' | 'tableData' | 'customQuills'

  customQuills?: CustomQuill[];
  showOnlyCustomQuills?: boolean;
  // priorityQuills?: string[]
  priorityQuills?: TTQuill[];

  // dataItems: AxisItemI[]
  hideZero?: boolean; // Always show zero ?

  dataMinMax?: any[]; // min/max of data to use in Axis scale
  idxMinMax?: number[]; // min/max indexes (Used for chart animations)
  inverted: boolean;
  // Ticks
  ticks?: boolean;
  ticksStyle?: LineStyle;
  tickPlacement?: string; // before | center | after
  // Axis Line
  line: boolean;
  lineStyle: LineStyle;
  // Grid Lines
  gridLines?: boolean; // Grid helper lines
  gridLinesStyle?: LineStyle; // Grid helper lines style
  tickToLabel?: number;
}
export interface AxisItemI {
  id?: string;
  value: number; // Numeric representation
  scaledValue: number;
  raw?: string; //  Original Value from 23° Data table
  dateValue?: Date; // In case, store the date object
  dayObj?: Dayjs; // Day.js Object
  label: string; // HTML of the Label ( Quill )
  bounds: BoundsI;
  //
  labelStyle?: any;
  line: boolean; // Axis Line
  lineStyle: LineStyle; // Axis Line Style
  tick?: boolean;
  tickStyle?: LineStyle;
  tickPlacement?: string;
  visible?: boolean;
  priority: number; // Label priority: 0 lowest - 3 hights atm
}

export enum AxisItemPriority {
  None = 0,
  Low = 1,
  High = 2,
  God = 3,
}

export interface AxisPaddingI {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export type DataType = 'line' | 'bars' | 'points' | 'area';

// Generic Point
export type Point = [number, number];

export type CurveType =
  | 'curveLinear'
  | 'curveStep'
  | 'curveStepBefore'
  | 'curveStepAfter'
  | 'curveBasis'
  | 'curveCardinal'
  | 'curveMonotoneX'
  | 'curveCatmullRom';

export interface DataI {
  label: string;
  type: DataType; // 'line', 'bars', 'points'
  xAxis: string; // ID of X Axis
  yAxis: string; // ID of Y Axis
  data: DataPointI[];
  style?: LineStyle; // SVGStyleI | CSSStyleI
  emptyLine?: EmptyLine;
  emptyLineStyle?: LineStyle;
  labelLine?: boolean;
  labelLineStyle?: LineStyle;
  baseColor?: string;
}

export interface DataPointI {
  label: string;
  x: number;
  y: number;
  point: Point; // Holds x,y pixel coordinates
  xDate?: Date;
  xDateObj?: Dayjs;
  xLabel?: string; // Normally x axis items will be strings
  row: any; // All fields and values coming from the raw data
}

export interface GridOpts {
  id?: string;
  data?: DataI[];
  bounds?: BoundsI;
  lineChart: LineChart;
  axisItems?: Array<AxisItemI[]>;
  contentItem?: any;
  meta?: {
    dataComplete: boolean;
  };
  labelSort?: string[];
  callbacks?: any;
  displayScale?: number;
  fontScale?: number;
}

export interface LabelComponentI {
  empty?: boolean;
  element: HTMLDivElement;
  bounds: BoundsI;
  idx: string;
}

export interface LabelsPropsI {
  enabled: boolean;
  labelsQuill: TTQuill;
  customQuills: CustomQuill[];
  layout: {
    margin: MarginI;
    display: string;
    style: any;
  };
  defaults?: {
    styles: any;
  };
  connectingLines: boolean;
  connectingLinesStyle: LineStyle;
  connectingLineMode?: string;
  distribute?: boolean;
}

export interface LegendPropsI {
  enabled: boolean;
  labelsQuill: TTQuill;
  customQuills: CustomQuill[];
  layout: {
    margin: MarginI;
    display: string; // always | desktop | mobile
    position: string; // top | bottom
    show: string; // same | line | icon | line_icon
  };
  defaults?: {
    styles: any;
  };
}

export interface ChartStyle {
  colors: string[];
  colorMap: any;
}

export interface ToolTipPropsI {
  enabled: boolean;
  layout: {
    margin: MarginI;
    box: {
      enabled: boolean;
      style: any;
    };
  };
  labelsQuill: TTQuill;
  customQuills: CustomQuill[];
  trigger: string; // 'click' | 'mouseover'
  connectingLine: boolean;
  connectingLineStyle: LineStyle;
}

export interface LineChart {
  layout: {
    margins: {
      outer: MarginI;
      inner: MarginI;
    };
    mobile: {
      breakpoint: number;
    };
  };
  lines: {
    defaults: {
      styles: LineStyle;
      emptyLineStyle?: LineStyle;
    };
    values: LineOpts[];
    emptyLine: EmptyLine;
  };
  areas: {
    defaults: {
      styles: AreaStyle;
    };
    values: AreaOpts[];
  };
  axis: AxisOpts[];
  animation: AnimationPropsI;
  legend: LegendPropsI;
  labels: LabelsPropsI;
  tooltip: ToolTipPropsI;
  annotations?: {
    defaults: {
      styles: Partial<AnnotationPropsI>;
    };
    values: Partial<AnnotationPropsI>[];
  };
  ranges?: {
    defaults: {
      styles: Partial<RangePropsI>;
    };
    values: Partial<RangePropsI>[];
  };
  palette: {
    // Maybe Temporary
    colors: string[];
    colorMap: any;
  };
}

export interface RangePropsI {
  // --- Mandatory
  id: string;
  from: string;
  to: string;
  raw?: string;
  // Layout
  direction: string; // horizontal | vertical
  type: string; // range | line
  // Text related
  quillEnabled?: boolean;
  quill?: TTQuill;
  quillAnchor?: string;
  quillRotation?: number;

  // --- Styling
  fill: string;
  fillOpacity: number;
  lineStyle?: LineStyle;
  // Responsive
  mobileLegend: boolean;
  mobileLegendStyle?: {
    padding: PaddingI;
    color: string;
    size: number;
    weight: string;
    fontFamily: string;
    width: number;
    shape: string; // circle, square, none
  };

  margin?: MarginI; // Text margin
  // Box
  border?: TTBorder;
  padding?: PaddingI; // Text padding
  background?: string; // Text background
  radius?: number;
  shadow?: Shadow;

  // --- State
  focused?: boolean; // Set Edit State
  touched?: boolean; // Styles applied
}

export type FnType = 'min' | 'max' | 'mean' | 'median' | 'mode';

export interface AnnotationPropsI {
  // --- Mandatory
  id: string;
  quill: TTQuill;
  raw: string;
  // Dot coordinates
  x: string;
  y: string;
  // Text coordinates
  xOffset: number;
  yOffset: number;
  width: number; // Text width

  // --- Styling
  // Responsiveness
  display: string; // always | desktop | mobile
  mobileLegend: boolean;
  mobileLegendStyle?: {
    padding: HTMLSpacing;
    columnGap: number;
    rowGap: number;
    color: string;
    size: number;
    weight: string;
    fontFamily: string;
    width: number; // Not used anymore
    shape: string; // circle, square, none
    layout?: string; // inline | block
  };
  onAnimation: string; // always | onPoint | onEnd
  smartAnchor?: {
    enabled: boolean;
    fn: FnType | null;
    source: string | null; // Line to take into account
  };
  padding: HTMLSpacing; // Text padding
  background: string; // Text background
  radius: number;
  shadow: Shadow;

  anchor: string; //top-left|top-center|top-right|center-left|...
  // border: string[] // top | left | bottom | right
  border: TTBorder;
  line: boolean;
  lineStyle: LineStyle;
  arrow: string; // 'none' | 'simple' | 'filled'
  curve: string; // 'linear' | 'under-spline' | 'over-spline' | 'over-step' | 'over-step'
  pointOffset: number; // Offset to point
  circle: boolean;
  circleStyle: LineStyle;
  circleSize: number;

  // --- State
  focused: boolean; // Set Edit State
  touched?: boolean; // Styles applied
}

export enum DataSource {
  URL = 'URL',
  REF = 'REF',
  NEW = 'NEW',
}

export type MarginIndexType = 'left' | 'right' | 'top' | 'bottom';

export type BorderType = 'none' | 'solid' | 'dotted' | 'dashed';